import {IUser, IUserAccess} from './index';

//#region INTERFACE IColleagues

/**
 * ### IColleagueUser
 * Interface representing a colleague user, combining user access with user data.
 *
 * This interface extends `IUserAccess` to provide the necessary user permissions along with the associated user data.
 *
 * @interface IColleagueUser
 * @extends {IUserAccess}
 * @example
 * const colleagueUser: IColleagueUser = {
 *   userData: {
 *     id: '12345',
 *     name: 'John Doe',
 *     email: 'johndoe@example.com'
 *   },
 *   access: {
 *     canEdit: true,
 *     canView: true
 *   }
 * };
 */
export interface IColleagueUser extends IUserAccess {
  /**
   * The associated user data object.
   *
   * This property contains detailed information about the user.
   *
   * @type {IUser}
   * @example
   * const userData: IUser = {
   *   id: '12345',
   *   name: 'John Doe',
   *   email: 'johndoe@example.com'
   * };
   */
  userData: IUser;

  /**
   * The last seen timestamp of the colleague user.
   *
   * This property indicates when the user was last active.
   *
   * @type {Date}
   * @example
   * const lastSeen: Date = new Date('2023-10-10T10:00:00Z');
   */
  lastSeen?: Date;

  lastSeenByStore?: ILastSeenByStore;
}

//#endregion

//#region INTERFACE  IColleagues
/**
 * ### IColleagues
 * Dictionary of colleagues indexed by user ID.
 *
 * This interface allows for the easy mapping of user IDs to colleague user data objects.
 *
 * @interface IColleagues
 * @example
 * const colleagues: IColleagues = {
 *   '12345': {
 *     userData: {
 *       id: '12345',
 *       name: 'John Doe',
 *       email: 'johndoe@example.com'
 *     },
 *     access: {
 *       canEdit: true,
 *       canView: true
 *     }
 *   },
 *   '67890': {
 *     userData: {
 *       id: '67890',
 *       name: 'Jane Smith',
 *       email: 'janesmith@example.com'
 *     },
 *     access: {
 *       canEdit: false,
 *       canView: true
 *     }
 *   }
 * };
 */
export type IColleagues = Record<string, IColleagueUser>;

/**
 * Interface for the timestamp entry in the last seen database
 */
export interface ILastSeenEntry {
  path: string[];
  time: { seconds: number, nanoseconds: number };
}

/**
 * Interface for the last seen map structure from Firebase
 */
export type ILastSeen = Record<string, ILastSeenEntry>;

/**
 * Interface for the restructured last seen timestamps by store
 */
export type ILastSeenByStore = Record<string, Record<string, Date>>;


//#endregion
